import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { inject } from '@angular/core';
import { AuthService } from './auth/auth.service';
import { ShellComponent } from './shell/shell.component';
import { moduleLicensed } from './api/module-licensed.guard';
import { LicenseType } from './api/gqty/schema.generated';

export const routes: Routes = [
  {
    // Public (unauthenticated) invitation-acceptance page reached from the
    // invite email link: ?token=… → set name + password → confirmAccount.
    // Must precede the empty-path shell route below: that route prefix-matches
    // every URL and its `**` child would otherwise swallow this path (redirecting
    // to /dashboard, or bouncing a logged-out invitee to the login flow).
    path: 'accept-invitation',
    loadComponent: () =>
      import('./auth/accept-invitation/accept-invitation-page').then((m) => m.AcceptInvitationPage),
  },
  {
    // Public (unauthenticated) email-change confirmation reached from the
    // confirmation email link: ?token=… → confirmEmailAddress. Same reason as
    // above for sitting before the shell route.
    path: 'confirm-email',
    loadComponent: () =>
      import('./auth/confirm-email-change/confirm-email-change-page').then(
        (m) => m.ConfirmEmailChangePage,
      ),
  },
  {
    // Public (unauthenticated) landing for a visitor the identity provider signed in but
    // who has no account on this tenant. Same reason as above for sitting before the
    // shell route — and it must stay outside the auth guard, or the guard would send it
    // back through the login flow that a live provider session replays right back here.
    path: 'no-access',
    loadComponent: () => import('./auth/no-access/no-access-page').then((m) => m.NoAccessPage),
  },
  {
    path: 'dev',
    canMatch: [() => environment.isDev],
    loadChildren: () => import('./dev-kit/dev-kit.routes').then((m) => m.DEV_KIT_ROUTES),
  },
  {
    path: '',
    canActivate: [
      async () => {
        const auth = inject(AuthService);
        try {
          await auth.loadMe();
          return true;
        } catch (error: unknown) {
          // Go to the login oAuth flow (rest)
          window.location.href = environment.apiBasePath + '/auth/login-flow';
          return false;
        }
      },
    ],
    component: ShellComponent,
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./sections/dashboard/dashboard-page').then((m) => m.DashboardPage),
      },
      {
        path: 'customers',
        data: { module: 'customers' },
        loadComponent: () =>
          import('./sections/gated/gated-module-page').then((m) => m.GatedModulePage),
      },
      {
        path: 'work-packages',
        loadChildren: () =>
          import('./sections/work-packages/routes').then((m) => m.WORK_PACKAGES_ROUTES),
      },
      {
        path: 'resources',
        loadChildren: () =>
          import('./sections/resources/routes').then((m) => m.RESOURCES_ROUTES),
      },
      {
        // Transportation: groupage consolidations + the hub network. Reached only
        // when the EVERPLAN_TRANSPORTATION module is active (guard below falls
        // through otherwise).
        path: 'transportation',
        canMatch: [moduleLicensed(LicenseType.EVERPLAN_TRANSPORTATION)],
        loadChildren: () =>
          import('./sections/transportation/routes').then((m) => m.TRANSPORTATION_ROUTES),
      },
      {
        // Reached only when the TRANSPORTATION module isn't licensed. The wildcard child
        // covers the section's deep links too (e.g. /transportation/consolidations), so a
        // locked Consolidations/Hubs entry lands on the upsell like every other gated section.
        path: 'transportation',
        children: [
          {
            path: '**',
            data: { module: 'transportation' },
            loadComponent: () =>
              import('./sections/gated/gated-module-page').then((m) => m.GatedModulePage),
          },
        ],
      },
      {
        path: 'planning',
        canMatch: [moduleLicensed(LicenseType.EVERPLAN_PLANNING)],
        loadComponent: () =>
          import('./sections/planning/planning-page').then((m) => m.PlanningPage),
      },
      {
        // Reached only when the PLANNING module isn't licensed (guard above fails).
        path: 'planning',
        data: { module: 'planning' },
        loadComponent: () =>
          import('./sections/gated/gated-module-page').then((m) => m.GatedModulePage),
      },
      // Moved into Settings → Debug; keep redirect for existing links.
      { path: 'planning-log', redirectTo: 'settings/planning-log-tab', pathMatch: 'full' },
      {
        path: 'financial',
        data: { module: 'financial' },
        loadComponent: () =>
          import('./sections/gated/gated-module-page').then((m) => m.GatedModulePage),
      },
      {
        // Live is the dedicated real-time execution console: the day timeline in
        // execution mode + the live map. Reached only when the EVERPLAN_LIVE
        // module is active (guard below falls through otherwise).
        path: 'live',
        canMatch: [moduleLicensed(LicenseType.EVERPLAN_LIVE)],
        loadComponent: () => import('./sections/live/live-page').then((m) => m.LivePage),
      },
      {
        // Reached only when the LIVE module isn't licensed (guard above fails).
        path: 'live',
        data: { module: 'live' },
        loadComponent: () =>
          import('./sections/gated/gated-module-page').then((m) => m.GatedModulePage),
      },
      {
        path: 'analytics',
        data: { module: 'analytics' },
        loadComponent: () =>
          import('./sections/gated/gated-module-page').then((m) => m.GatedModulePage),
      },
      {
        path: 'ai',
        data: { module: 'ai' },
        loadComponent: () =>
          import('./sections/gated/gated-module-page').then((m) => m.GatedModulePage),
      },
      {
        path: 'inventory',
        data: { module: 'inventory' },
        loadComponent: () =>
          import('./sections/gated/gated-module-page').then((m) => m.GatedModulePage),
      },
      // Moved into Settings → Debug; keep redirect for existing links.
      { path: 'executor-events', redirectTo: 'settings/executor-events-tab', pathMatch: 'full' },
      {
        path: 'settings',
        loadChildren: () =>
          import('./sections/settings/routes').then((m) => m.SETTINGS_ROUTES),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./sections/profile/profile-page').then((m) => m.ProfilePage),
      },
      // Catch-all: an unknown authenticated path (e.g. /work-orders) would
      // otherwise resolve to a blank shell, so send it to the dashboard.
      { path: '**', redirectTo: 'dashboard' },
    ],
  },
];
